import {
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitStatus,
  OrderResponseDto,
  RailVisitResponseDto,
  TruckVisitDto,
  TruckVisitWithOrdersDto,
  VesselVisitDto,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { IInspectContainerFormData } from '@planning/pages/GateClerk/Components/InspectContainer'
import GateClerkDocumentsPage from '@planning/pages/GateClerk/GateClerkDocumentsPage'
import {
  GateClerkDocumentsPageViewStore,
  RoutingInfoData,
} from '@planning/pages/GateClerk/Stores/GateClerkDocumentsPageViewStore'
import {
  orderService,
  railVisitService,
  truckVisitService,
  vesselVisitService,
} from '@planning/services'
import { useNavigationStore } from '@planning/stores'
import { GetEasiestContainerToReachFunc } from '@planning/stores/gateClerk/SuggestedContainerDto'
import { ValidateOutboundOrderFunc } from '@planning/stores/gateControl/ValidateOutboundDto'
import { canOrderProceed } from '@planning/utils/order-utils'
import { getHoldEnforcementResult, masterHoldTypes } from '@planning/utils/hold-enforcement'
import { useTranslate } from '@tolgee/react'
import { Loader } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useGateControlMessageHandler } from '../GateControl/hooks/useGateControlMessageHandler'
import { GateClerkSelectTruckVisit } from './Components/Organisms/GateClerkSelectTruckVisit'
import { GangSelection } from './GangSelection'
import { GateInForm, IGateInFormData } from './GateInForm'
import { GateOutForm } from './GateOutForm'
import { SearchArrivedTruckVisit } from './SearchArrivedTruckVisit'
import { SelectOperation } from './SelectOperation'

export interface GateClerkHomePageExternalDeps {
  getJourneyPositions: (
    journeyIds: number[],
    isForPlannedPosition?: boolean,
  ) => Promise<{ journeyId: number; position: string }[]>
  validateAllocationCapacity: (inboundOrderIds: number[]) => Promise<number[]>
  getEasiestContainerToReach: GetEasiestContainerToReachFunc
  getGeneralCargoReservations: (
    orderId: number,
  ) => Promise<Array<{ generalCargoArea?: string | null; generalCargoLocation?: string | null }>>
  renderOutboundValidation: ValidateOutboundOrderFunc
  renderRouteMiniMap: (visitId?: number) => Promise<React.ReactElement | null>
  gangs: Array<{ id: number; name: string }>
  onFetchGangs: () => Promise<void>
}

export const GateClerkHomePage = observer((props: GateClerkHomePageExternalDeps) => {
  const {
    getJourneyPositions,
    validateAllocationCapacity,
    getEasiestContainerToReach,
    getGeneralCargoReservations,
    renderOutboundValidation,
    renderRouteMiniMap,
    gangs,
    onFetchGangs,
  } = props

  const { t } = useTranslate()
  const { appViewStore, gateClerkViewStore, containerDamageReportViewStore, tenantStore, holdItemStore } =
    usePlanningStore()
  const { getExceptionMessage, getSuccessMessage } = useGateControlMessageHandler()

  const { notificationStore } = gateClerkViewStore
  const navigationStore = useNavigationStore()

  notificationStore.upsertNnOrders = gateClerkViewStore.gateInViewStore.upsertNNROrder

  const gateClerkDocumentsPageViewStore = useMemo(() => new GateClerkDocumentsPageViewStore(), [])

  useEffect(() => {
    const fetchData = async () => {
      await containerDamageReportViewStore.fetch()
    }
    fetchData()
  }, [containerDamageReportViewStore])

  const getLinkedOrderArrivedOrInOperationVisitName = async (
    order: OrderResponseDto,
    isPickUp?: boolean,
  ) => {
    let arrivedOrInOperationVisitName

    if (order.linkedOrderId || isPickUp) {
      const linkedOrder: OrderResponseDto = order.linkedOrderId
        ? await orderService.getById(order.linkedOrderId)
        : await orderService.getById(order.id)
      if (!linkedOrder || !linkedOrder.carrierVisitId) return arrivedOrInOperationVisitName
      if (linkedOrder.carrierVisitType === CarrierType.Vessel) {
        const vesselVisit: VesselVisitDto = await vesselVisitService.getById(
          linkedOrder.carrierVisitId,
        )
        if (
          vesselVisit.status === CarrierVisitStatus.Arrived ||
          vesselVisit.status === CarrierVisitStatus.InOperation
        ) {
          arrivedOrInOperationVisitName = vesselVisit.identifier
            ? `${t('vessel', 'vessel')}: ${vesselVisit.identifier}`
            : undefined
        }
      }
      if (linkedOrder.carrierVisitType === CarrierType.Truck) {
        const { data: truckVisits }: { data: TruckVisitDto[] } = await truckVisitService.getByIds([
          linkedOrder.carrierVisitId,
        ])
        if (truckVisits[0].ata && !truckVisits[0].atd) {
          arrivedOrInOperationVisitName = `${t('truck', 'truck')}: ${truckVisits[0].identifier}`
        }
      }
      if (linkedOrder.carrierVisitType === CarrierType.Train) {
        const railVisit: RailVisitResponseDto = await railVisitService.getById(
          linkedOrder.carrierVisitId,
        )
        if (
          railVisit.status === CarrierVisitStatus.Arrived ||
          railVisit.status === CarrierVisitStatus.InOperation
        ) {
          const railVisitDetails: RailVisitResponseDto = await railVisitService.getById(
            linkedOrder.carrierVisitId,
          )
          arrivedOrInOperationVisitName = `${t('train', 'Train')}: ${railVisitDetails.identifier} ${linkedOrder.railTrackName}-${t('no', 'no')}.${linkedOrder.waggon} `
        }
      }
    }
    return arrivedOrInOperationVisitName
  }

  const populateGateOutFormData = async (visit: TruckVisitDto) => {
    const { gateOutViewStore } = gateClerkViewStore
    gateOutViewStore.setVisit(visit)
    await gateOutViewStore.fetch()
  }

  const onSelectTruckVisitForGateOut = async (visit: TruckVisitDto) => {
    await populateGateOutFormData(visit)

    gateClerkViewStore.arrivedTruckVisitSearchStore.setFilter('')

    navigationStore.push(
      <GateOutForm
        store={gateClerkViewStore.gateOutViewStore}
        gateClerkDocumentsPageViewStore={gateClerkDocumentsPageViewStore}
      />,
    )
  }

  const onGateInFormSubmit = async (data: IGateInFormData) => {
    try {
      navigationStore.push(<Loader show={true} />)
      // [Printouts] TODO: disable for dev
      const appointment = await notificationStore.createGateInRequest(data, true)
      const truckVisitId = appointment?.truckVisitId ?? data.truckVisitId
      const gateInData = { ...data, truckVisitId }

      if (gateClerkDocumentsPageViewStore.routingInfoData.length > 0) {
        const miniMap = await renderRouteMiniMap(truckVisitId)
        gateClerkDocumentsPageViewStore.setMiniMap(miniMap)

        navigationStore.push(
          <GateClerkDocumentsPage
            viewStore={gateClerkDocumentsPageViewStore}
            direction={CarrierVisitDirection.Inbound}
            gateInData={gateInData}
          />,
        )
      } else {
        gateClerkViewStore.gateInViewStore.reset()
        gateClerkDocumentsPageViewStore.reset()
        navigationStore.goTo(1)
      }

      appViewStore.setShowAlert('success', getSuccessMessage())
    } catch (error) {
      navigationStore.goTo(-2)
      appViewStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  const fillRoutingInfoData = async () => {
    const dropOffJourneyIds = gateClerkViewStore.gateInViewStore.dropOffOrders
      .filter(o => o.journeyId)
      .map(o => o.journeyId!)

    const pickUpJourneyIds = gateClerkViewStore.gateInViewStore.pickUpOrders
      .filter(o => o.journeyId)
      .map(o => o.journeyId!)

    const [inboundPositions, pickUpPositions] = await Promise.all([
      getJourneyPositions(dropOffJourneyIds, true),
      getJourneyPositions(pickUpJourneyIds, false),
    ])

    const master = masterHoldTypes.get()
    const dropOffData: RoutingInfoData[] = await Promise.all(
      gateClerkViewStore.gateInViewStore.dropOffOrders
        .filter(o => !!o.containerNumber)
        .filter(o =>
          canOrderProceed({
            allowGateInWhenUnitNotOnTerminal: tenantStore.allowGateInWhenUnitNotOnTerminal,
            direction: o.direction,
            hasBlockingHold: getHoldEnforcementResult(
              o.journeyId != null
                ? (holdItemStore.holdsByContainerJourneyId[o.journeyId] ?? o.holds ?? [])
                : (o.holds ?? []),
              o.direction === CarrierVisitDirection.Inbound ? 'GateIn' : 'GateOut',
              o.direction,
              master,
            ).isBlocking,
            isOnTerminal: o.isOnTerminal,
            skipReleaseOrder: tenantStore.skipReleaseOrder,
          }),
        )
        .map(async o => ({
          containerNumber: o.containerNumber!,
          blockName:
            inboundPositions.find(info => info.journeyId === o.journeyId)?.position ?? undefined,
          visitLabel: await getLinkedOrderArrivedOrInOperationVisitName(o),
          type: 'dropOff' as const,
        })),
    )

    const pickUpData: RoutingInfoData[] = await Promise.all(
      gateClerkViewStore.gateInViewStore.pickUpOrders
        .filter(o => !!o.containerNumber)
        .filter(o =>
          canOrderProceed({
            allowGateInWhenUnitNotOnTerminal: tenantStore.allowGateInWhenUnitNotOnTerminal,
            direction: o.direction,
            hasBlockingHold: getHoldEnforcementResult(
              o.journeyId != null
                ? (holdItemStore.holdsByContainerJourneyId[o.journeyId] ?? o.holds ?? [])
                : (o.holds ?? []),
              o.direction === CarrierVisitDirection.Inbound ? 'GateIn' : 'GateOut',
              o.direction,
              master,
            ).isBlocking,
            isOnTerminal: o.isOnTerminal,
            skipReleaseOrder: tenantStore.skipReleaseOrder,
          }),
        )
        .map(async o => ({
          containerNumber: o.containerNumber!,
          blockName:
            pickUpPositions.find(info => info.journeyId === o.journeyId)?.position ?? undefined,
          visitLabel: await getLinkedOrderArrivedOrInOperationVisitName(o, true),
          type: 'pickUp' as const,
        })),
    )

    const isGeneralCargoOrder = (order: IInspectContainerFormData) =>
      !order.containerNumber && order.commodityId !== null && order.commodityId !== undefined

    const buildGeneralCargoRoutingInfo = async (
      orders: IInspectContainerFormData[],
      type: 'generalCargoDropOff' | 'generalCargoPickUp',
    ): Promise<RoutingInfoData[]> =>
      Promise.all(
        orders.filter(isGeneralCargoOrder).map(async order => {
          const reservations = await getGeneralCargoReservations(order.id)
          const reservationLocations = reservations
            .map(r => `${r.generalCargoArea ?? ''} ${r.generalCargoLocation ?? ''}`.trim())
            .filter(Boolean)
          const location =
            reservationLocations.length > 0 ? [...new Set(reservationLocations)].join(', ') : ''

          return {
            type,
            generalCargoOrderId: order.id,
            generalCargoLocation: location,
          }
        }),
      )

    const [dropOffGeneralCargoData, pickUpGeneralCargoData] = await Promise.all([
      buildGeneralCargoRoutingInfo(
        gateClerkViewStore.gateInViewStore.dropOffOrders,
        'generalCargoDropOff',
      ),
      buildGeneralCargoRoutingInfo(
        gateClerkViewStore.gateInViewStore.pickUpOrders,
        'generalCargoPickUp',
      ),
    ])

    gateClerkDocumentsPageViewStore.setRoutingInfoData([
      ...dropOffData,
      ...pickUpData,
      ...dropOffGeneralCargoData,
      ...pickUpGeneralCargoData,
    ])

    gateClerkDocumentsPageViewStore.setMiniMap(null)
  }

  const onGateInWithRoutingInfo = async (data: IGateInFormData) => {
    navigationStore.push(<Loader show={true} />)

    await fillRoutingInfoData()

    await onGateInFormSubmit(data)
  }

  const onSelectVisitForTruckAppointmentGateIn = (visit: TruckVisitWithOrdersDto | null) => {
    if (!visit) return

    gateClerkViewStore.gateInViewStore.reset()

    visit.inboundOrders?.forEach(order => {
      const journey = visit.containerJourneys?.find(journey => journey.inboundOrderId === order.id)

      const data: IInspectContainerFormData = {
        ...order,

        carrierType: CarrierType.Truck,
        damages: [],
        journeyId: journey?.id,
      }
      gateClerkViewStore.gateInViewStore.upsertDropOffOrder(data)
    })

    visit.outboundOrders?.forEach(order => {
      const isNonNumericOrder = order.nonNumericOrderId && !order.containerNumber
      const journey = visit.containerJourneys?.find(journey => journey.outboundOrderId === order.id)

      isNonNumericOrder
        ? gateClerkViewStore.gateInViewStore.upsertNNROrderByOrder(order)
        : gateClerkViewStore.gateInViewStore.upsertPickUpOrder({
            ...order,
            carrierType: CarrierType.Truck,
            damages: [],
            journeyId: journey?.id,
          })
    })

    navigationStore.push(
      <GateInForm
        truckVisitWithOrders={visit}
        store={gateClerkViewStore.gateInViewStore}
        onSubmit={data => {
          if (visit.identifier && visit.id) {
            data.truckVisitId = visit.id
            data.truckPlate = visit.identifier
            data.externalPortGatePassRef = visit.externalPortGatePassRef
            data.truckCompanyId = visit.truckCompany?.id
            data.externalDriverId = visit.driver?.id
            data.driverName = visit.driver?.name ?? undefined
            data.driverID = visit.driver?.driverID ?? undefined
            data.truckCompanyName = visit.truckCompany?.name ?? undefined
            onGateInWithRoutingInfo(data)
          }
        }}
      />,
    )
  }

  const onSelectVisitForTruckAppointmentGateOut = (visit: TruckVisitWithOrdersDto | null) => {
    if (!visit || !visit.id || !visit.containers) return

    //TODO: Create method to map TruckVisitWithOrdersDto to TruckVisitDto and also use it in TruckVisitAggregationStore
    const truckVisit: TruckVisitDto = {
      id: visit.id,
      status: visit.status ?? CarrierVisitStatus.Expected,
      carrierIds: [],
      cargoType: visit.cargoType ?? 'Unknown',
      driverName: visit.driver?.name,
      driverId: visit.driver?.driverID ?? '0',
      isCancelled: visit.isCancelled,
      eta: visit.eta ?? undefined,
      ata: visit.ata ?? undefined,
      etd: visit.etd ?? undefined,
      atd: visit.atd ?? undefined,
      identifier: visit.identifier,
      truckCompanyName: visit.truckCompany?.name ?? 'Unknown',
    }

    gateClerkViewStore.gateOutViewStore.reset()
    gateClerkViewStore.gateOutViewStore.setVisit(truckVisit)

    const orders = []

    if (visit.inboundOrders) orders.push(...visit.inboundOrders)
    if (visit.outboundOrders) orders.push(...visit.outboundOrders)
    gateClerkViewStore.gateOutViewStore.processOrders(orders, visit.containers)

    navigationStore.push(
      <GateOutForm
        truckVisitWithOrders={visit}
        store={gateClerkViewStore.gateOutViewStore}
        gateClerkDocumentsPageViewStore={gateClerkDocumentsPageViewStore}
      />,
    )
  }

  const onSelectOperationForTruckAppointment = (operation: CarrierVisitDirection) => {
    if (operation === CarrierVisitDirection.Inbound)
      navigationStore.push(
        <GateClerkSelectTruckVisit
          label={t('gateIn', 'Gate in')}
          direction='Inbound'
          onSelectVisit={onSelectVisitForTruckAppointmentGateIn}
        />,
      )
    else
      navigationStore.push(
        <GateClerkSelectTruckVisit
          label={t('gateOut', 'Gate Out')}
          direction='Outbound'
          onSelectVisit={onSelectVisitForTruckAppointmentGateOut}
        />,
      )
  }

  const onSelectOperationNext = (operation: CarrierVisitDirection) => {
    if (operation === CarrierVisitDirection.Inbound)
      navigationStore.push(
        <GateInForm
          store={gateClerkViewStore.gateInViewStore}
          onSubmit={onGateInWithRoutingInfo}
          validateAllocationSpace={validateAllocationCapacity}
        />,
      )
    else {
      gateClerkViewStore.arrivedTruckVisitSearchStore.setFilter('')
      navigationStore.push(
        <SearchArrivedTruckVisit
          store={gateClerkViewStore.arrivedTruckVisitSearchStore}
          onClick={onSelectTruckVisitForGateOut}
        />,
      )
    }
  }

  gateClerkViewStore.gateInViewStore.validateOutboundRequest = useCallback(
    renderOutboundValidation,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  notificationStore.getEasiestContainerToReachFunc = getEasiestContainerToReach

  return (
    <GangSelection
      store={gateClerkViewStore}
      gangs={gangs}
      onFetchGangs={onFetchGangs}
      onContinueClick={() => {
        navigationStore.push(
          <SelectOperation
            store={gateClerkViewStore}
            navigateNext={
              tenantStore.skipTruckAppointment
                ? onSelectOperationNext
                : onSelectOperationForTruckAppointment
            }
          />,
        )
      }}
    />
  )
})
